/**
 * Resolves and shares the current principal (email + role) across the app.
 *
 * The role only gates *client-side UX* (hiding admin-only nav/controls). The
 * Worker independently enforces authorization on every endpoint (SPEC §3), so
 * a tampered client can never escalate — it just sees buttons that 403.
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError, type Me } from "./api";

interface RoleState {
  me: Me | null;
  loading: boolean;
  /** Set when the Access session is invalid / API unreachable. */
  authError: ApiError | null;
  isAdmin: boolean;
  reload: () => void;
}

const RoleCtx = createContext<RoleState | null>(null);

export function RoleProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<ApiError | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .getMe()
      .then((m) => {
        if (!cancelled) {
          setMe(m);
          setAuthError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setMe(null);
          setAuthError(e instanceof ApiError ? e : new ApiError(0, "unknown", String(e)));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  const value: RoleState = {
    me,
    loading,
    authError,
    isAdmin: me?.role === "admin",
    reload: () => setTick((t) => t + 1),
  };

  return <RoleCtx.Provider value={value}>{children}</RoleCtx.Provider>;
}

export function useRole(): RoleState {
  const ctx = useContext(RoleCtx);
  if (!ctx) throw new Error("useRole must be used within <RoleProvider>");
  return ctx;
}
